import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  TextField,
  useTheme,
  Button,
  styled,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from '@mui/material';

import {
  DarkMode,
  LightMode,
  VerticalSplit,
  SwapHoriz,
  Palette,
  FormatColorText,
  Close as CloseIcon,
  Save,
} from '@mui/icons-material';

import SettingsIcon from '@mui/icons-material/Settings';
import { SketchPicker } from 'react-color';
import { usePdfSettings } from './PdfSettingsContext.js';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

/* =========================================================
   CUSTOM SCROLLBAR
========================================================= */

const CustomScrollbar = styled('div')(({ theme }) => ({
  '&::-webkit-scrollbar': {
    width: '6px',
    background: 'transparent',
  },

  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'transparent',
    borderRadius: '3px',
  },

  '&:hover::-webkit-scrollbar-thumb': {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? 'rgba(255,255,255,0.2)'
        : 'rgba(0,0,0,0.2)',
  },

  scrollbarWidth: 'thin',
  marginRight: '-15px',
  paddingRight: '10px',
  overflowY: 'auto',
  overflowX: 'hidden',
}));

/* =========================================================
   SECTION
========================================================= */

const StyledSection = styled(Box)(({ theme }) => ({
  marginBottom: 24,
  padding: '16px',
  borderRadius: '12px',

  backgroundColor:
    theme.palette.mode === 'dark'
      ? 'rgba(255,255,255,0.05)'
      : 'rgba(0,0,0,0.02)',

  border: `1px solid ${
    theme.palette.mode === 'dark'
      ? 'rgba(255,255,255,0.1)'
      : 'rgba(0,0,0,0.08)'
  }`,

  transition: 'all 0.3s ease',

  '&:hover': {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? 'rgba(255,255,255,0.08)'
        : 'rgba(0,0,0,0.04)',
  },
}));

/* =========================================================
   OPTION BUTTON
========================================================= */

const OptionButton = styled(
  Button,
  {
    shouldForwardProp: (prop) =>
      prop !== 'selected' && prop !== 'selectedColor',
  }
)(({ theme, selected, selectedColor }) => ({
  flex: 1,
  padding: '12px 16px',
  borderRadius: '10px',
  textTransform: 'none',
  fontSize: '0.95rem',
  fontWeight: selected ? 600 : 500,

  border: `2px solid ${
    selected
      ? selectedColor
      : theme.palette.mode === 'dark'
        ? 'rgba(255,255,255,0.1)'
        : 'rgba(0,0,0,0.1)'
  }`,

  backgroundColor: selected
    ? theme.palette.mode === 'dark'
      ? 'rgba(255,255,255,0.05)'
      : 'rgba(0,0,0,0.02)'
    : 'transparent',

  color: selected
    ? selectedColor
    : theme.palette.text.primary,

  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',

  '&:hover': {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? 'rgba(255,255,255,0.1)'
        : 'rgba(0,0,0,0.05)',

    borderColor: selectedColor,

    transform: 'translateY(-2px)',

    boxShadow: `0 4px 12px ${selectedColor}20`,
  },
}));

/* =========================================================
   COLOR PICKER
========================================================= */

const ColorPickerContainer = styled(Box)(({ theme }) => ({
  padding: '12px',
  borderRadius: '10px',

  backgroundColor:
    theme.palette.mode === 'dark'
      ? 'rgba(0,0,0,0.3)'
      : 'rgba(0,0,0,0.02)',

  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'column',

  '& .sketch-picker': {
    boxShadow: 'none !important',
    borderRadius: '8px !important',
  },
}));

/* =========================================================
   SECTION TITLE
========================================================= */

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '0.95rem',
  marginBottom: 12,

  display: 'flex',
  alignItems: 'center',
  gap: 8,

  color: theme.palette.text.primary,

  letterSpacing: '0.5px',
  textTransform: 'uppercase',
}));

/* =========================================================
   DONE BUTTON
========================================================= */

const DoneButton = styled(Button)(({ theme }) => ({
  marginTop: '12px',
  alignSelf: 'flex-end',

  padding: '6px 16px',
  minWidth: 'auto',

  borderRadius: '8px',

  fontSize: '0.8rem',
  fontWeight: 600,

  textTransform: 'none',
}));

/* =========================================================
   SETTINGS SIDEBAR
========================================================= */

const SettingsSidebar = ({
  open,
  onClose,

  darkMode,
  onToggleDarkMode,

  primaryColor,
  onChangePrimaryColor,

  layoutType,
  onToggleLayoutType,

  onChangePdfThemeColor,
  onChangePdfTextColor,

  onSaveSettings,

  updateAdmissionPrefix,
  onChangeAdmissionPrefix,
  admissionPrefix,
}) => {
  const theme = useTheme();

  const isDark = theme.palette.mode === 'dark';

  const {
    pdfThemeColor,
    pdfTextColor,
    updatePdfThemeColor,
    updatePdfTextColor,
  } = usePdfSettings();

  /* =========================================================
     TEMP SETTINGS
========================================================= */

  const [tempSettings, setTempSettings] = useState({
    tempDarkMode: darkMode,
    tempPrimaryColor: primaryColor,
    tempLayoutType: layoutType,

    // Independent PDF settings
    tempPdfThemeColor: pdfThemeColor,
    tempPdfTextColor: pdfTextColor,

    tempAdmissionPrefix: admissionPrefix,
  });

  /* =========================================================
     COLOR PICKERS
========================================================= */

  const [showPdfThemeColor, setShowPdfThemeColor] =
    useState(false);

  const [showPdfTextColor, setShowPdfTextColor] =
    useState(false);

  const [showThemeColor, setShowThemeColor] =
    useState(false);

  /* =========================================================
     MODULE / FORM
========================================================= */

  const [moduleFormOptions, setModuleFormOptions] =
    useState([]);

  const [selectedModuleForm, setSelectedModuleForm] =
    useState('');

  const [loadingModules, setLoadingModules] =
    useState(false);

  /* =========================================================
     PREFIX
========================================================= */

  const [existingPrefixId, setExistingPrefixId] =
    useState(null);

  const [loadingPrefix, setLoadingPrefix] =
    useState(false);

  /* =========================================================
     SEARCH
========================================================= */

  const [searchTerm, setSearchTerm] = useState('');

  const [openDropdown, setOpenDropdown] =
    useState(false);

  /* =========================================================
     FILTER MODULE / FORM OPTIONS
========================================================= */

  const filteredOptions = React.useMemo(() => {
    if (!searchTerm.trim()) {
      return moduleFormOptions;
    }

    const term = searchTerm.toLowerCase().trim();

    return moduleFormOptions.filter((opt) =>
      opt.label.toLowerCase().includes(term)
    );
  }, [searchTerm, moduleFormOptions]);

  /* =========================================================
     FETCH MODULES / FORMS
========================================================= */

  useEffect(() => {
    const fetchModulesForms = async () => {
      setLoadingModules(true);

      try {
        const accessToken =
          localStorage.getItem('accessToken');

        if (!accessToken) {
          return;
        }

        const response = await fetch(
          `${API_BASE_URL}/api/settings/modules-forms/`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );

        if (response.ok) {
          const data = await response.json();

          const options = [];

          Object.entries(data).forEach(
            ([module, forms]) => {
              forms.forEach((form) => {
                options.push({
                  module,
                  form,
                  value: `${module}-${form}`,
                  label: `${module} → ${form}`,
                });
              });
            }
          );

          setModuleFormOptions(options);
        } else {
          console.error(
            'Failed to load modules-forms'
          );
        }
      } catch (err) {
        console.error(
          'Error fetching modules-forms:',
          err
        );
      } finally {
        setLoadingModules(false);
      }
    };

    if (open) {
      fetchModulesForms();
    } else {
      setSelectedModuleForm('');
      setOpenDropdown(false);
      setSearchTerm('');
    }
  }, [open]);

  /* =========================================================
     LOAD EXISTING PREFIX
========================================================= */

  useEffect(() => {
    if (!selectedModuleForm) {
      setTempSettings((prev) => ({
        ...prev,
        tempAdmissionPrefix: '',
      }));

      setExistingPrefixId(null);

      return;
    }

    const loadPrefix = async () => {
      setLoadingPrefix(true);

      setTempSettings((prev) => ({
        ...prev,
        tempAdmissionPrefix: '',
      }));

      setExistingPrefixId(null);

      try {
        const accessToken =
          localStorage.getItem('accessToken');

        if (!accessToken) {
          return;
        }

        const [
          selectedModule,
          selectedForm,
        ] = selectedModuleForm.split('-');

        console.log(
          `Fetching fresh prefix list for: ${selectedModule} → ${selectedForm}`
        );

        const listResponse = await fetch(
          `${API_BASE_URL}/api/settings/prefixes/`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );

        if (!listResponse.ok) {
          console.error(
            'List fetch failed',
            listResponse.status
          );

          return;
        }

        const listData =
          await listResponse.json();

        const prefixes = Array.isArray(listData)
          ? listData
          : [];

        const match = prefixes.find((item) => {
          const itemModule =
            item.module ||
            item.app_label ||
            item.content_type?.app_label ||
            item.application ||
            '';

          const itemForm =
            item.form ||
            item.model ||
            item.content_type?.model ||
            item.model_name ||
            '';

          return (
            itemModule.toLowerCase().trim() ===
              selectedModule
                .toLowerCase()
                .trim() &&
            itemForm.toLowerCase().trim() ===
              selectedForm
                .toLowerCase()
                .trim()
          );
        });

        if (!match || !match.id) {
          console.log(
            `No match found for ${selectedModule} - ${selectedForm}`
          );

          console.log(
            'Available items:',
            prefixes.map((p) => ({
              id: p.id,
              module:
                p.module ||
                p.app_label ||
                p.content_type?.app_label,
              form:
                p.form ||
                p.model ||
                p.content_type?.model,
            }))
          );

          return;
        }

        const prefixId = match.id;

        console.log(
          `Match found → ID: ${prefixId}`
        );

        const detailUrl =
          `${API_BASE_URL}/api/settings/prefixes/${prefixId}/`;

        const detailResponse = await fetch(
          detailUrl,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );

        if (detailResponse.ok) {
          const detailData =
            await detailResponse.json();

          const prefixValue =
            detailData.prefix ||
            detailData.prefix_code ||
            detailData.code ||
            detailData.value ||
            '';

          if (prefixValue) {
            setTempSettings((prev) => ({
              ...prev,
              tempAdmissionPrefix:
                prefixValue.toUpperCase(),
            }));

            setExistingPrefixId(prefixId);
          }
        } else {
          console.error(
            'Detail fetch failed',
            detailResponse.status
          );
        }
      } catch (err) {
        console.error(
          'loadPrefix error:',
          err
        );
      } finally {
        setLoadingPrefix(false);
      }
    };

    loadPrefix();
  }, [selectedModuleForm]);

  /* =========================================================
     SYNC SETTINGS WHEN DRAWER OPENS
========================================================= */

  useEffect(() => {
    if (open) {
      if (primaryColor) {
        localStorage.setItem(
          'primaryColor',
          primaryColor
        );
      }

      setTempSettings({
        tempDarkMode: darkMode,
        tempPrimaryColor: primaryColor,
        tempLayoutType: layoutType,

        // PDF Background is independent from Theme Color
        tempPdfThemeColor: pdfThemeColor,

        // PDF Text Color is independent
        tempPdfTextColor: pdfTextColor,

        tempAdmissionPrefix: admissionPrefix,
      });
    }
  }, [
    open,
    darkMode,
    primaryColor,
    layoutType,
    pdfThemeColor,
    pdfTextColor,
    admissionPrefix,
  ]);

  /* =========================================================
     HANDLERS
========================================================= */

  const handleTempPdfThemeColor = (color) => {
    setTempSettings((prev) => ({
      ...prev,
      tempPdfThemeColor: color.hex,
    }));
  };

  const handleTempPdfTextColor = (color) => {
    setTempSettings((prev) => ({
      ...prev,
      tempPdfTextColor: color.hex,
    }));
  };

  const handleTempPrimaryColor = (color) => {
    setTempSettings((prev) => ({
      ...prev,
      tempPrimaryColor: color.hex,
    }));
  };

  const handleTempAdmissionPrefix = (prefix) => {
    setTempSettings((prev) => ({
      ...prev,
      tempAdmissionPrefix:
        prefix.toUpperCase(),
    }));
  };

  const handleTempDarkMode = (mode) => {
    setTempSettings((prev) => ({
      ...prev,
      tempDarkMode: mode,
    }));
  };

  const handleTempLayoutType = (type) => {
    setTempSettings((prev) => ({
      ...prev,
      tempLayoutType: type,
    }));
  };

  /* =========================================================
     SAVE SETTINGS
========================================================= */

  const handleSave = async () => {
    try {
      /* ---------------------------------------------
         DISPLAY MODE
      --------------------------------------------- */

      if (
        tempSettings.tempDarkMode !==
        darkMode
      ) {
        onToggleDarkMode(
          tempSettings.tempDarkMode
        );
      }

      /* ---------------------------------------------
         PRIMARY / GLOBAL THEME COLOR
         
         IMPORTANT:
         This ONLY changes the application theme.
         It does NOT change PDF Background.
      --------------------------------------------- */

      if (
        tempSettings.tempPrimaryColor !==
        primaryColor
      ) {
        onChangePrimaryColor(
          tempSettings.tempPrimaryColor
        );

        localStorage.setItem(
          'primaryColor',
          tempSettings.tempPrimaryColor
        );

        /*
         * IMPORTANT:
         * Do NOT call updatePdfThemeColor() here.
         *
         * Global Theme Color and PDF Background
         * are completely independent.
         */
      }

      /* ---------------------------------------------
         LAYOUT
      --------------------------------------------- */

      if (
        tempSettings.tempLayoutType !==
        layoutType
      ) {
        onToggleLayoutType(
          tempSettings.tempLayoutType
        );
      }

      /* ---------------------------------------------
         PDF BACKGROUND COLOR
         
         This is the ONLY place where the
         PDF Background selected in Settings
         is saved globally.
      --------------------------------------------- */

      if (
        tempSettings.tempPdfThemeColor !==
        pdfThemeColor
      ) {
        updatePdfThemeColor(
          tempSettings.tempPdfThemeColor
        );
      }

      /* ---------------------------------------------
         PDF TEXT COLOR
         
         This is saved independently.
      --------------------------------------------- */

      if (
        tempSettings.tempPdfTextColor !==
        pdfTextColor
      ) {
        updatePdfTextColor(
          tempSettings.tempPdfTextColor
        );
      }

      /* ---------------------------------------------
         PREFIX
      --------------------------------------------- */

      if (
        selectedModuleForm &&
        tempSettings.tempAdmissionPrefix
      ) {
        const accessToken =
          localStorage.getItem('accessToken');

        if (!accessToken) {
          console.error(
            'Access token not found'
          );

          return;
        }

        const [
          module,
          form,
        ] = selectedModuleForm.split('-');

        const prefixValue =
          tempSettings.tempAdmissionPrefix
            .trim()
            .toUpperCase();

        let response;

        /* ---------------------------------------------
           UPDATE EXISTING PREFIX
        --------------------------------------------- */

        if (existingPrefixId) {
          response = await fetch(
            `${API_BASE_URL}/api/settings/prefixes/${existingPrefixId}/`,
            {
              method: 'PUT',

              headers: {
                Authorization:
                  `Bearer ${accessToken}`,
                'Content-Type':
                  'application/json',
              },

              body: JSON.stringify({
                module,
                form,
                prefix: prefixValue,
              }),
            }
          );
        }

        /* ---------------------------------------------
           CREATE NEW PREFIX
        --------------------------------------------- */

        else {
          response = await fetch(
            `${API_BASE_URL}/api/settings/prefixes/`,
            {
              method: 'POST',

              headers: {
                Authorization:
                  `Bearer ${accessToken}`,
                'Content-Type':
                  'application/json',
              },

              body: JSON.stringify({
                module,
                form,
                prefix: prefixValue,
              }),
            }
          );
        }

        /* ---------------------------------------------
           PREFIX SUCCESS
        --------------------------------------------- */

        if (response.ok) {
          console.log(
            `Prefix ${
              existingPrefixId
                ? 'updated'
                : 'created'
            } successfully`
          );

          onChangeAdmissionPrefix(
            prefixValue
          );

          setTempSettings((prev) => ({
            ...prev,
            tempAdmissionPrefix: '',
          }));

          setExistingPrefixId(null);
        }

        /* ---------------------------------------------
           PREFIX ERROR
        --------------------------------------------- */

        else {
          const errorText =
            await response.text();

          console.error(
            `Failed to ${
              existingPrefixId
                ? 'update'
                : 'create'
            } prefix`,
            errorText
          );

          return;
        }
      }

      /* ---------------------------------------------
         PARENT SAVE CALLBACK
      --------------------------------------------- */

      if (onSaveSettings) {
        await onSaveSettings();
      }

      /* ---------------------------------------------
         CLOSE DRAWER AFTER SAVE
      --------------------------------------------- */

      onClose();

    } catch (error) {
      console.error(
        'Error saving settings:',
        error
      );
    }
  };

  /* =========================================================
     HAS CHANGES
========================================================= */

  const hasChanges =
    tempSettings.tempDarkMode !== darkMode ||
    tempSettings.tempPrimaryColor !==
      primaryColor ||
    tempSettings.tempLayoutType !==
      layoutType ||
    tempSettings.tempPdfThemeColor !==
      pdfThemeColor ||
    tempSettings.tempPdfTextColor !==
      pdfTextColor ||
    tempSettings.tempAdmissionPrefix !==
      admissionPrefix;

  /* =========================================================
     RETURN
========================================================= */

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          zIndex: 12000,

          width: {
            xs: '90vw',
            sm: 320,
            md: 340,
          },

          backgroundImage: isDark
            ? 'linear-gradient(135deg, #0f1419 0%, #1a1f2e 100%)'
            : 'linear-gradient(135deg, #fafbfc 0%, #f5f7fa 100%)',

          boxShadow: isDark
            ? '0 8px 32px rgba(0,0,0,0.4)'
            : '0 8px 32px rgba(0,0,0,0.1)',

          overflow: 'visible',
        },
      }}
      sx={{
        zIndex: 12000,
      }}
    >
      {/* =====================================================
          HEADER
      ===================================================== */}

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
          pb: 1.5,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Box
            sx={{
              p: 1,
              borderRadius: '10px',
              backgroundColor:
                primaryColor + '20',

              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <SettingsIcon
              sx={{
                color: primaryColor,
                fontSize: 24,
              }}
            />
          </Box>

          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: '1.1rem',
              letterSpacing: '0.5px',
            }}
          >
            Settings
          </Typography>
        </Box>

        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            transition: 'all 0.2s ease',

            '&:hover': {
              backgroundColor:
                primaryColor + '20',

              color: primaryColor,
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <Divider
        sx={{
          opacity: 0.3,
          mb: 1,
        }}
      />

      {/* =====================================================
          CONTENT
      ===================================================== */}

      <CustomScrollbar
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          maxHeight:
            'calc(100vh - 180px)',
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1,
          }}
        >
          {/* =================================================
              DISPLAY MODE
          ================================================= */}

          <StyledSection>
            <SectionTitle>
              <DarkMode fontSize="small" />
              Display Mode
            </SectionTitle>

            <Box
              sx={{
                display: 'flex',
                gap: 1,
              }}
            >
              <OptionButton
                selected={
                  !tempSettings.tempDarkMode
                }
                selectedColor={primaryColor}
                onClick={() =>
                  handleTempDarkMode(false)
                }
                startIcon={<LightMode />}
              >
                Light
              </OptionButton>

              <OptionButton
                selected={
                  tempSettings.tempDarkMode
                }
                selectedColor={primaryColor}
                onClick={() =>
                  handleTempDarkMode(true)
                }
                startIcon={<DarkMode />}
              >
                Dark
              </OptionButton>
            </Box>
          </StyledSection>

          {/* =================================================
              GLOBAL THEME COLOR
          ================================================= */}

          <StyledSection>
            <SectionTitle>
              <Palette fontSize="small" />
              Theme Color
            </SectionTitle>

            <Box
              sx={{
                position: 'relative',
              }}
            >
              <Box
                onClick={() =>
                  setShowThemeColor(
                    !showThemeColor
                  )
                }
                sx={{
                  p: 1,
                  height: '40px',
                  borderRadius: '10px',

                  backgroundColor:
                    tempSettings.tempPrimaryColor,

                  cursor: 'pointer',

                  transition:
                    'all 0.3s ease',

                  border:
                    `2px solid ${primaryColor}`,

                  '&:hover': {
                    transform:
                      'scale(1.02)',

                    boxShadow:
                      `0 4px 16px ${tempSettings.tempPrimaryColor}40`,
                  },

                  display: 'flex',
                  justifyContent:
                    'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color:
                      theme.palette.getContrastText(
                        tempSettings.tempPrimaryColor
                      ),
                  }}
                >
                  {
                    tempSettings.tempPrimaryColor
                  }
                </Typography>

                {showThemeColor && (
                  <DoneButton
                    variant="contained"
                    onClick={(e) => {
                      e.stopPropagation();

                      setShowThemeColor(
                        false
                      );
                    }}
                    size="small"
                    sx={{
                      ml: 1,
                    }}
                  >
                    Done
                  </DoneButton>
                )}
              </Box>

              {showThemeColor && (
                <ColorPickerContainer
                  sx={{
                    mt: 2,
                  }}
                >
                  <SketchPicker
                    color={
                      tempSettings.tempPrimaryColor
                    }
                    onChangeComplete={
                      handleTempPrimaryColor
                    }
                    disableAlpha
                  />
                </ColorPickerContainer>
              )}
            </Box>
          </StyledSection>

          {/* =================================================
              PREFIX SETTINGS
          ================================================= */}

          <StyledSection>
            <SectionTitle>
              <Typography variant="inherit">
                📋
              </Typography>

              Prefix
            </SectionTitle>

            {/* MODULE / FORM SELECT */}

            <FormControl
              fullWidth
              size="small"
              sx={{
                mb: 2.5,
              }}
              disabled={loadingModules}
            >
              <InputLabel
                id="module-form-select-label"
                sx={{
                  color: isDark
                    ? '#e0e0e0'
                    : '#333',

                  fontWeight: 400,

                  '&.Mui-focused': {
                    color: primaryColor,
                  },

                  '&.Mui-disabled': {
                    color: isDark
                      ? '#aaaaaa'
                      : '#666',

                    opacity: 1,
                  },
                }}
              >
                Select Module / Form
              </InputLabel>

              <Select
                labelId="module-form-select-label"
                value={selectedModuleForm}
                label="Select Module / Form"
                open={openDropdown}
                onOpen={() => {
                  setOpenDropdown(true);
                  setSearchTerm('');
                }}
                onClose={() => {
                  setOpenDropdown(false);
                  setSearchTerm('');
                }}
                onChange={(e) => {
                  setSelectedModuleForm(
                    e.target.value
                  );

                  setOpenDropdown(false);
                  setSearchTerm('');
                }}
                sx={{
                  borderRadius: '8px',

                  '&:hover fieldset': {
                    borderColor:
                      primaryColor,
                  },

                  '&.Mui-focused fieldset': {
                    borderColor:
                      primaryColor,

                    borderWidth: 2,
                  },
                }}
                MenuProps={{
                  disablePortal: false,

                  sx: {
                    zIndex: 13000,

                    '& .MuiPaper-root': {
                      borderRadius: '10px',

                      boxShadow:
                        '0 8px 32px rgba(0,0,0,0.25)',

                      marginTop: '4px',

                      maxHeight: '60vh',

                      overflowY: 'auto',
                    },
                  },

                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                  },

                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left',
                  },
                }}
              >
                {/* SEARCH BAR */}

                {openDropdown && (
                  <Box
                    sx={{
                      position: 'sticky',
                      top: 0,

                      backgroundColor:
                        isDark
                          ? '#1e1e1e'
                          : '#ffffff',

                      zIndex: 10,

                      borderBottom:
                        `1px solid ${
                          isDark
                            ? 'rgba(255,255,255,0.12)'
                            : 'rgba(0,0,0,0.12)'
                        }`,

                      p: 1,
                    }}
                    onMouseDown={(e) =>
                      e.stopPropagation()
                    }
                    onClick={(e) =>
                      e.stopPropagation()
                    }
                    onKeyDown={(e) =>
                      e.stopPropagation()
                    }
                  >
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Search module or form..."
                      autoFocus
                      value={searchTerm}
                      onChange={(e) =>
                        setSearchTerm(
                          e.target.value
                        )
                      }
                      onKeyDown={(e) => {
                        e.stopPropagation();

                        if (
                          e.key === 'Escape'
                        ) {
                          setSearchTerm('');

                          e.preventDefault();

                          setOpenDropdown(false);
                        }

                        if (
                          e.key === 'Enter'
                        ) {
                          e.preventDefault();
                        }
                      }}
                      onMouseDown={(e) =>
                        e.stopPropagation()
                      }
                      sx={{
                        '& .MuiOutlinedInput-root':
                          {
                            borderRadius:
                              '6px',

                            backgroundColor:
                              isDark
                                ? 'rgba(255,255,255,0.08)'
                                : 'rgba(0,0,0,0.04)',
                          },
                      }}
                    />
                  </Box>
                )}

                {/* LOADING */}

                {loadingModules ? (
                  <MenuItem disabled>
                    <CircularProgress
                      size={20}
                      sx={{
                        mr: 1.5,
                      }}
                    />

                    Loading modules...
                  </MenuItem>
                ) : filteredOptions.length ===
                  0 ? (
                  <MenuItem disabled>
                    {searchTerm.trim()
                      ? 'No matching results'
                      : 'No modules/forms available'}
                  </MenuItem>
                ) : (
                  filteredOptions.map(
                    (option) => (
                      <MenuItem
                        key={option.value}
                        value={option.value}
                      >
                        {option.label}
                      </MenuItem>
                    )
                  )
                )}
              </Select>
            </FormControl>

            {/* PREFIX INPUT */}

            <TextField
              fullWidth
              variant="outlined"
              size="small"
              value={
                tempSettings.tempAdmissionPrefix
              }
              onChange={(e) =>
                handleTempAdmissionPrefix(
                  e.target.value
                )
              }
              placeholder="e.g., ADM, SDM"
              label="Prefix Code"
              disabled={
                !selectedModuleForm ||
                loadingPrefix
              }
              InputProps={{
                endAdornment:
                  loadingPrefix ? (
                    <CircularProgress
                      size={20}
                    />
                  ) : null,
              }}
              sx={{
                '& .MuiOutlinedInput-root':
                  {
                    borderRadius: '8px',

                    transition:
                      'all 0.3s ease',

                    backgroundColor:
                      isDark
                        ? 'rgba(255,255,255,0.06)'
                        : 'rgba(0,0,0,0.04)',

                    '&:hover fieldset': {
                      borderColor:
                        primaryColor,
                    },

                    '&.Mui-focused fieldset':
                      {
                        borderColor:
                          primaryColor,

                        borderWidth: 2,
                      },
                  },

                '& .MuiInputBase-input':
                  {
                    color: isDark
                      ? '#e0e0e0'
                      : '#111111',

                    WebkitTextFillColor:
                      isDark
                        ? '#e0e0e0 !important'
                        : '#111111 !important',

                    '&::placeholder': {
                      color: isDark
                        ? '#aaaaaa'
                        : '#777777',

                      opacity: 0.7,
                    },
                  },

                '& .MuiInputBase-input.Mui-disabled':
                  {
                    WebkitTextFillColor:
                      isDark
                        ? '#cccccc !important'
                        : '#555555 !important',

                    color: isDark
                      ? '#cccccc !important'
                      : '#555555 !important',

                    opacity: 0.9,
                  },

                '& .MuiInputLabel-root':
                  {
                    color: isDark
                      ? '#dddddd'
                      : '#222222',

                    '&.Mui-focused': {
                      color:
                        primaryColor,
                    },

                    '&.Mui-disabled': {
                      color: isDark
                        ? '#bbbbbb'
                        : '#666666',

                      opacity: 0.95,
                    },
                  },
              }}
            />
          </StyledSection>

          {/* =================================================
              PDF BACKGROUND
          ================================================= */}

          <StyledSection>
            <SectionTitle>
              <Palette fontSize="small" />
              PDF Background
            </SectionTitle>

            <Box
              sx={{
                position: 'relative',
              }}
            >
              <Box
                onClick={() =>
                  setShowPdfThemeColor(
                    !showPdfThemeColor
                  )
                }
                sx={{
                  p: 1,
                  height: '40px',
                  borderRadius: '10px',

                  backgroundColor:
                    tempSettings.tempPdfThemeColor,

                  cursor: 'pointer',

                  transition:
                    'all 0.3s ease',

                  // Border uses app Theme Color,
                  // but the actual PDF Background
                  // remains completely independent.
                  border:
                    `2px solid ${primaryColor}`,

                  '&:hover': {
                    transform:
                      'scale(1.02)',

                    boxShadow:
                      `0 4px 16px ${tempSettings.tempPdfThemeColor}40`,
                  },

                  display: 'flex',

                  justifyContent:
                    'space-between',

                  alignItems: 'center',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color:
                      theme.palette.getContrastText(
                        tempSettings.tempPdfThemeColor
                      ),
                  }}
                >
                  {
                    tempSettings.tempPdfThemeColor
                  }
                </Typography>

                {showPdfThemeColor && (
                  <DoneButton
                    variant="contained"
                    onClick={(e) => {
                      e.stopPropagation();

                      setShowPdfThemeColor(
                        false
                      );
                    }}
                    size="small"
                    sx={{
                      ml: 1,
                    }}
                  >
                    Done
                  </DoneButton>
                )}
              </Box>

              {showPdfThemeColor && (
                <ColorPickerContainer
                  sx={{
                    mt: 2,
                  }}
                >
                  <SketchPicker
                    color={
                      tempSettings.tempPdfThemeColor
                    }
                    onChangeComplete={
                      handleTempPdfThemeColor
                    }
                    disableAlpha
                  />
                </ColorPickerContainer>
              )}
            </Box>
          </StyledSection>

          {/* =================================================
              PDF TEXT COLOR
          ================================================= */}

          <StyledSection>
            <SectionTitle>
              <FormatColorText fontSize="small" />
              PDF Text Color
            </SectionTitle>

            <Box
              sx={{
                position: 'relative',
              }}
            >
              <Box
                onClick={() =>
                  setShowPdfTextColor(
                    !showPdfTextColor
                  )
                }
                sx={{
                  p: 1,
                  height: '40px',
                  borderRadius: '10px',

                  backgroundColor:
                    tempSettings.tempPdfTextColor,

                  cursor: 'pointer',

                  transition:
                    'all 0.3s ease',

                  border:
                    `2px solid ${primaryColor}`,

                  '&:hover': {
                    transform:
                      'scale(1.02)',

                    boxShadow:
                      `0 4px 16px ${tempSettings.tempPdfTextColor}40`,
                  },

                  display: 'flex',

                  justifyContent:
                    'space-between',

                  alignItems: 'center',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color:
                      theme.palette.getContrastText(
                        tempSettings.tempPdfTextColor
                      ),
                  }}
                >
                  {
                    tempSettings.tempPdfTextColor
                  }
                </Typography>

                {showPdfTextColor && (
                  <DoneButton
                    variant="contained"
                    onClick={(e) => {
                      e.stopPropagation();

                      setShowPdfTextColor(
                        false
                      );
                    }}
                    size="small"
                    sx={{
                      ml: 1,
                    }}
                  >
                    Done
                  </DoneButton>
                )}
              </Box>

              {showPdfTextColor && (
                <ColorPickerContainer
                  sx={{
                    mt: 2,
                  }}
                >
                  <SketchPicker
                    color={
                      tempSettings.tempPdfTextColor
                    }
                    onChangeComplete={
                      handleTempPdfTextColor
                    }
                    disableAlpha
                  />
                </ColorPickerContainer>
              )}
            </Box>
          </StyledSection>

          {/* =================================================
              SIDEBAR LAYOUT
          ================================================= */}

          <StyledSection>
            <SectionTitle>
              <VerticalSplit fontSize="small" />
              Sidebar Layout
            </SectionTitle>

            <Box
              sx={{
                display: 'flex',
                gap: 1,
              }}
            >
              <OptionButton
                selected={
                  tempSettings.tempLayoutType ===
                  'full'
                }
                selectedColor={primaryColor}
                onClick={() =>
                  handleTempLayoutType(
                    'full'
                  )
                }
                startIcon={
                  <VerticalSplit />
                }
              >
                Full
              </OptionButton>

              <OptionButton
                selected={
                  tempSettings.tempLayoutType ===
                  'collapse'
                }
                selectedColor={primaryColor}
                onClick={() =>
                  handleTempLayoutType(
                    'collapse'
                  )
                }
                startIcon={
                  <SwapHoriz />
                }
              >
                Collapse
              </OptionButton>
            </Box>
          </StyledSection>
        </Box>
      </CustomScrollbar>

      {/* =====================================================
          FOOTER / SAVE
      ===================================================== */}

      <Box
        sx={{
          p: 2,

          borderTop:
            `1px solid ${
              isDark
                ? 'rgba(255,255,255,0.1)'
                : 'rgba(0,0,0,0.1)'
            }`,

          background:
            isDark
              ? 'rgba(0,0,0,0.2)'
              : 'rgba(255,255,255,0.5)',

          backdropFilter:
            'blur(10px)',
        }}
      >
        <Button
          variant="contained"
          fullWidth
          onClick={handleSave}
          disabled={!hasChanges}
          startIcon={<Save />}
          sx={{
            py: 1.2,

            borderRadius: '10px',

            fontSize: '0.95rem',

            fontWeight: 600,

            textTransform: 'none',

            background: hasChanges
              ? `linear-gradient(
                  135deg,
                  ${primaryColor} 0%,
                  ${primaryColor}dd 100%
                )`
              : isDark
                ? 'rgba(255,255,255,0.1)'
                : 'rgba(0,0,0,0.1)',

            color: hasChanges
              ? 'white'
              : theme.palette.text.disabled,

            transition:
              'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',

            '&:hover:not(:disabled)': {
              boxShadow:
                `0 8px 24px ${primaryColor}40`,

              transform:
                'translateY(-2px)',
            },

            '&:disabled': {
              cursor: 'not-allowed',
            },
          }}
        >
          Save Settings
        </Button>
      </Box>
    </Drawer>
  );
};

export default SettingsSidebar;